'use client';

import { useState, useEffect, useRef } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { 
  Calendar, 
  Clock, 
  User, 
  ArrowLeft, 
  Share2, 
  Twitter, 
  Facebook, 
  Linkedin, 
  Copy,
  Tag,
  ChevronRight,
  FileText
} from 'lucide-react';
import { BlogPost, RelatedPost } from '@/types/blog';

interface BlogPostClientProps {
  post: BlogPost;
  relatedPosts: RelatedPost[];
}

export default function BlogPostClient({ post, relatedPosts }: BlogPostClientProps) {
  const [readingProgress, setReadingProgress] = useState(0);
  const [isShareMenuOpen, setIsShareMenuOpen] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [mainImageError, setMainImageError] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const smoothProgressRef = useRef(0);

  // Format date
  const formatDate = (dateString: string) => {
    return new Intl.DateTimeFormat('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    }).format(new Date(dateString));
  };

  // Calculate reading progress with smooth interpolation
  useEffect(() => {
    let ticking = false;
    let animationFrame: number;

    const updateReadingProgress = () => {
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const scrollTop = window.scrollY;
      
      // Calculate progress based on entire page scroll
      const maxScroll = documentHeight - windowHeight;
      
      // Calculate target progress as a percentage (0-100)
      let targetProgress = 0;
      if (maxScroll > 0) {
        targetProgress = Math.min(Math.max((scrollTop / maxScroll) * 100, 0), 100);
      }
      
      // Smooth interpolation for fluid animation
      const currentProgress = smoothProgressRef.current;
      const difference = targetProgress - currentProgress;
      const smoothedProgress = currentProgress + (difference * 0.1); // Smooth interpolation factor
      
      smoothProgressRef.current = smoothedProgress;
      setReadingProgress(smoothedProgress);
      
      // Continue animation if there's still a significant difference
      if (Math.abs(difference) > 0.1) {
        animationFrame = requestAnimationFrame(updateReadingProgress);
      } else {
        smoothProgressRef.current = targetProgress;
        setReadingProgress(targetProgress);
        ticking = false;
      }
    };

    const requestTick = () => {
      if (!ticking) {
        ticking = true;
        animationFrame = requestAnimationFrame(updateReadingProgress);
      }
    };

    // Use passive listener for better performance
    window.addEventListener('scroll', requestTick, { passive: true });
    window.addEventListener('resize', requestTick, { passive: true });
    
    // Initial calculation
    updateReadingProgress();

    return () => {
      window.removeEventListener('scroll', requestTick);
      window.removeEventListener('resize', requestTick);
      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }
    };
  }, []);

  // Share functions
  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';
  const shareTitle = post.title;

  const shareLinks = {
    twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareTitle)}&via=Droquimar`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy: ', err);
    }
  };

  // Related Post Image Component
  const RelatedPostImage = ({ relatedPost }: { relatedPost: RelatedPost }) => {
    const [imageError, setImageError] = useState(false);
    const [imageLoaded, setImageLoaded] = useState(false);

    const hasValidImage = relatedPost.featuredImage && 
      relatedPost.featuredImage.trim() !== '' && 
      !imageError;

    return (
      <div className="relative aspect-video bg-gray-200 overflow-hidden">
        {hasValidImage ? (
          <>
            <Image
              src={relatedPost.featuredImage!}
              alt={relatedPost.title}
              fill
              className={`object-cover group-hover:scale-105 transition-transform duration-300 ${
                imageLoaded ? 'opacity-100' : 'opacity-0'
              }`}
              sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
              onError={() => setImageError(true)}
              onLoad={() => setImageLoaded(true)}
            />
            {!imageLoaded && (
              <div className="absolute inset-0 bg-gray-200 animate-pulse" />
            )}
          </>
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
            <FileText className="h-12 w-12 text-gray-400" />
          </div>
        )}
      </div>
    );
  };

  return (
    <article className="max-w-4xl mx-auto px-4 py-8">
      {/* Reading Progress Bar */}
      <div className="fixed top-0 left-0 w-full h-1 bg-gray-100 z-50 shadow-sm">
        <div 
          className="h-full bg-gradient-to-r from-brand-primary via-brand-primary to-brand-light relative overflow-hidden shadow-sm"
          style={{ 
            width: `${readingProgress}%`,
            transition: 'width 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
            willChange: 'width',
            transform: 'translateZ(0)' // Force hardware acceleration
          }}
        >
          {/* Subtle pulsing effect while reading */}
          {readingProgress > 0 && readingProgress < 100 && (
            <div className="absolute inset-0 bg-white/10 animate-pulse" />
          )}
        </div>
      </div>

      {/* Back to Blog */}
      <div className="mb-8">
        <Link 
          href="/blog"
          className="inline-flex items-center gap-2 text-brand-primary hover:text-brand-primary-hover transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Volver al blog
        </Link>
      </div>

      {/* Article Header */}
      <header className="mb-8">
        {/* Categories */}
        <div className="flex flex-wrap gap-2 mb-4">
          {post.categories.map((category) => (
            <Link
              key={category}
              href={`/blog?category=${category.toLowerCase()}`}
              className="inline-flex items-center gap-1 text-sm font-medium text-brand-primary bg-brand-primary/10 hover:bg-brand-primary/20 px-3 py-1 rounded-full transition-colors"
            >
              <Tag className="h-3 w-3" />
              {category}
            </Link>
          ))}
          {post.featured && (
            <span className="inline-flex items-center text-sm font-medium text-yellow-700 bg-yellow-100 px-3 py-1 rounded-full">
              ⭐ Destacado
            </span>
          )}
        </div>

        {/* Title */}
        <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
          {post.title}
        </h1>

        {/* Excerpt */}
        <p className="text-xl text-gray-600 mb-8 leading-relaxed">
          {post.excerpt}
        </p>

        {/* Meta Information */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 pb-8 border-b border-gray-200">
          <div className="flex items-center gap-6">
            {/* Author */}
            {post.author.name && (
              <div className="flex items-center gap-3">
                {post.author.avatar && (
                  <Image
                    src={post.author.avatar}
                    alt={post.author.name}
                    width={48}
                    height={48}
                    className="rounded-full"
                  />
                )}
                <div>
                  <div className="flex items-center gap-1 text-gray-900 font-medium">
                    <User className="h-4 w-4" />
                    {post.author.name}
                  </div>
                  {post.author.role && (
                    <p className="text-sm text-gray-600">{post.author.role}</p>
                  )}
                </div>
              </div>
            )}

            {/* Date and Reading Time */}
            <div className="flex items-center gap-4 text-sm text-gray-600">
              <div className="flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                <time dateTime={post.publishedAt}>
                  {formatDate(post.publishedAt)}
                </time>
              </div>
              <div className="flex items-center gap-1">
                <Clock className="h-4 w-4" />
                <span>{post.readingTime.text}</span>
              </div>
            </div>
          </div>

          {/* Share Button */}
          <div className="relative">
            <button
              onClick={() => setIsShareMenuOpen(!isShareMenuOpen)}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
            >
              <Share2 className="h-4 w-4" />
              Compartir
            </button>

            {/* Share Menu */}
            {isShareMenuOpen && (
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border border-gray-200 py-2 z-10">
                <a
                  href={shareLinks.twitter}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <Twitter className="h-4 w-4 text-blue-400" />
                  Twitter
                </a>
                <a
                  href={shareLinks.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <Facebook className="h-4 w-4 text-blue-600" />
                  Facebook
                </a>
                <a
                  href={shareLinks.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <Linkedin className="h-4 w-4 text-blue-700" />
                  LinkedIn
                </a>
                <button
                  onClick={copyToClipboard}
                  className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors w-full"
                >
                  <Copy className="h-4 w-4 text-gray-500" />
                  {copySuccess ? 'Copiado!' : 'Copiar enlace'}
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Featured Image */}
      {post.featuredImage && post.featuredImage.trim() !== '' && !mainImageError && (
        <div className="relative aspect-video mb-12 rounded-xl overflow-hidden shadow-lg">
          <Image
            src={post.featuredImage}
            alt={post.title}
            fill
            className="object-cover"
            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 80vw, 1200px"
            priority
            onError={() => setMainImageError(true)}
          />
        </div>
      )}

      {/* Article Content */}
      <div 
        ref={contentRef}
        className="prose prose-lg prose-gray max-w-none mb-12"
        dangerouslySetInnerHTML={{ __html: post.content }}
        style={{
          // Custom prose styles
          lineHeight: '1.8',
        }}
      />

      {/* Tags */}
      {post.tags.length > 0 && (
        <div className="mb-12 pt-8 border-t border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Etiquetas</h3>
          <div className="flex flex-wrap gap-2">
            {post.tags.map((tag) => (
              <Link
                key={tag}
                href={`/blog?tag=${tag}`}
                className="inline-flex items-center gap-1 text-sm text-gray-600 bg-gray-100 hover:bg-gray-200 px-3 py-1 rounded-full transition-colors"
              >
                <Tag className="h-3 w-3" />
                {tag}
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <section className="mt-16">
          <h2 className="text-2xl font-bold text-gray-900 mb-8">Artículos relacionados</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {relatedPosts.map((relatedPost) => (
              <article
                key={relatedPost.id}
                className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-all hover:-translate-y-1 group"
              >
                <Link href={`/blog/${relatedPost.slug}`} className="block">
                  {/* Image */}
                  <RelatedPostImage relatedPost={relatedPost} />

                  {/* Content */}
                  <div className="p-6">
                    {/* Categories */}
                    <div className="flex flex-wrap gap-1 mb-3">
                      {relatedPost.categories.slice(0, 2).map((category) => (
                        <span
                          key={category}
                          className="text-xs font-medium text-brand-primary bg-brand-primary/10 px-2 py-1 rounded-full"
                        >
                          {category}
                        </span>
                      ))}
                    </div>

                    {/* Title */}
                    <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2 group-hover:text-brand-primary transition-colors">
                      {relatedPost.title}
                    </h3>

                    {/* Excerpt */}
                    <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                      {relatedPost.excerpt}
                    </p>

                    {/* Meta */}
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <span>{formatDate(relatedPost.publishedAt)}</span>
                      <span>{relatedPost.readingTime.text}</span>
                    </div>
                  </div>
                </Link>
              </article>
            ))}
          </div>

          {/* View All Posts */}
          <div className="text-center mt-8">
            <Link
              href="/blog"
              className="inline-flex items-center gap-2 px-6 py-3 bg-brand-primary text-white rounded-md hover:bg-brand-primary-hover transition-colors"
            >
              Ver todos los artículos
              <ChevronRight className="h-4 w-4" />
            </Link>
          </div>
        </section>
      )}

      {/* Click outside to close share menu */}
      {isShareMenuOpen && (
        <div
          className="fixed inset-0 z-5"
          onClick={() => setIsShareMenuOpen(false)}
        />
      )}
    </article>
  );
}